import { Activity, ArrowRight } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import type { AuditLog } from '~/api/types'
import { actionLabel, actorLabel, targetLabel } from '~/lib/audit-labels'
import { Card } from '~/components/ui/card'
import { EmptyState } from '~/components/ui/empty-state'
import { Skeleton } from '~/components/ui/skeleton'

// 절대 시각을 "n분 전" 상대 표기로. 하루 이상이면 날짜.
function relativeTime(iso: string): string {
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return iso
    const sec = Math.floor((Date.now() - d.getTime()) / 1000)
    if (sec < 60) return '방금'
    if (sec < 3600) return `${Math.floor(sec / 60)}분 전`
    if (sec < 86400) return `${Math.floor(sec / 3600)}시간 전`
    return d.toLocaleDateString('ko-KR')
}

// 4-우) 최근 활동 — 감사로그 최근 5건.
export function ActivityLogSection({ recent }: { recent: AuditLog[] | null }) {
    const navigate = useNavigate()
    return (
        <Card className='p-5'>
            <div className='flex items-start justify-between gap-2'>
                <div>
                    <h3 className='krds-title text-foreground'>최근 활동</h3>
                    <p className='text-muted-foreground mt-0.5 text-sm'>
                        감사로그 최근 5건
                    </p>
                </div>
                <button
                    type='button'
                    onClick={() => navigate('/audit')}
                    className='text-primary hover:text-primary/80 focus-visible:ring-ring/40 flex shrink-0 items-center gap-0.5 rounded-[var(--radius-sm)] text-[12px] font-medium outline-none focus-visible:ring-2'>
                    전체보기 <ArrowRight className='size-3.5' />
                </button>
            </div>
            <div className='mt-4'>
                {recent === null ? (
                    <div className='space-y-3'>
                        {[0, 1, 2, 3].map((i) => (
                            <Skeleton key={i} className='h-4' />
                        ))}
                    </div>
                ) : recent.length === 0 ? (
                    <EmptyState
                        icon={Activity}
                        title='최근 활동이 없어요.'
                        className='py-8'
                    />
                ) : (
                    <ul className='divide-border -my-2 divide-y'>
                        {recent.map((log) => (
                            <li
                                key={log.id}
                                className='flex items-center gap-3 py-2'>
                                <div className='min-w-0 flex-1'>
                                    <div
                                        className='text-foreground truncate text-[13px]'
                                        title={log.actor}>
                                        <span className='font-medium'>
                                            {actorLabel(log)}
                                        </span>{' '}
                                        <span className='text-muted-foreground'>
                                            {actionLabel(log.action)}
                                        </span>
                                    </div>
                                    <div className='text-muted-foreground truncate text-[12px]'>
                                        {targetLabel(log)}
                                    </div>
                                </div>
                                <span className='text-muted-foreground shrink-0 text-[12px] tabular-nums'>
                                    {relativeTime(log.createdAt)}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </Card>
    )
}
